import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { OptimisticLocalStore } from 'convex/browser'
import type { FunctionReturnType } from 'convex/server'

import { optimisticHabitCompletion } from '@/lib/habit-optimistic'
import { patchNode } from '@/lib/nodes-optimistic'

type Tree = NonNullable<FunctionReturnType<typeof api.nodes.getGoalTree>>
type UserGoal = FunctionReturnType<typeof api.nodes.getUserGoals>[number]
type FullGoal = FunctionReturnType<typeof api.nodes.getGoalsFullTree>[number]

export type GoalPatch = {
  title?: string
  status?: Tree['status']
  isPublic?: boolean
  isReadOnly?: boolean
  timeBudget?: Tree['timeBudget'] | null
  freezes?: UserGoal['freezes']
  hasCover?: boolean
}

const pick = <T extends object>(patch: GoalPatch, keys: (keyof GoalPatch)[]) =>
  Object.fromEntries(keys.filter((k) => k in patch).map((k) => [k, patch[k] ?? undefined])) as T

// Goal-level fields across getGoalTree, getUserGoals and getGoalsFullTree.
export function patchGoal(store: OptimisticLocalStore, goalId: Id<'nodes'>, patch: GoalPatch) {
  const done = patch.status === 'done' ? { progress: 100 } : {}
  const treePatch = pick<Partial<Tree>>(patch, [
    'title',
    'status',
    'isPublic',
    'isReadOnly',
    'timeBudget',
    'hasCover',
  ])
  for (const { args, value } of store.getAllQueries(api.nodes.getGoalTree)) {
    if (value?._id === goalId)
      store.setQuery(api.nodes.getGoalTree, args, { ...value, ...treePatch, ...done })
  }
  const goals = store.getQuery(api.nodes.getUserGoals, {})
  if (goals) {
    const p = pick<Partial<UserGoal>>(patch, ['title', 'status', 'isPublic', 'freezes'])
    store.setQuery(
      api.nodes.getUserGoals,
      {},
      goals.map((g) => (g._id === goalId ? { ...g, ...p, ...done } : g)),
    )
  }
  const full = store.getQuery(api.nodes.getGoalsFullTree, {})
  if (full) {
    const p = pick<Partial<FullGoal>>(patch, ['title', 'status'])
    store.setQuery(
      api.nodes.getGoalsFullTree,
      {},
      full.map((g) => (g._id === goalId ? { ...g, ...p, ...done } : g)),
    )
  }
}

// Drop a goal from the goal lists (the open goal page navigates away itself).
export function removeGoal(store: OptimisticLocalStore, goalId: Id<'nodes'>) {
  const goals = store.getQuery(api.nodes.getUserGoals, {})
  if (goals)
    store.setQuery(
      api.nodes.getUserGoals,
      {},
      goals.filter((g) => g._id !== goalId),
    )
  const full = store.getQuery(api.nodes.getGoalsFullTree, {})
  if (full)
    store.setQuery(
      api.nodes.getGoalsFullTree,
      {},
      full.filter((g) => g._id !== goalId),
    )
}

// AI replan: only the queued state is predictable; the server fills context/changes.
export function markReplanPending(store: OptimisticLocalStore, goalId: Id<'nodes'>) {
  const cur = store.getQuery(api.replanQueue.getPendingForGoal, { goalId })
  if (!cur?.pending)
    store.setQuery(
      api.replanQueue.getPendingForGoal,
      { goalId },
      { pending: true, context: [], changes: null, richChanges: null },
    )
  const goals = store.getQuery(api.nodes.getUserGoals, {})
  if (goals)
    store.setQuery(
      api.nodes.getUserGoals,
      {},
      goals.map((g) => (g._id === goalId ? { ...g, needsReplanning: true } : g)),
    )
}

type Habit = FunctionReturnType<typeof api.nodes.getHabits>[number]

// Habit-only fields (anchor, autoCheck, measure) that patchNode doesn't carry.
export function patchHabit(
  store: OptimisticLocalStore,
  habitId: Id<'nodes'>,
  patch: Partial<Habit> | ((h: Habit) => Partial<Habit>),
) {
  const habits = store.getQuery(api.nodes.getHabits, {})
  if (!habits?.some((h) => h._id === habitId)) return
  store.setQuery(
    api.nodes.getHabits,
    {},
    habits.map((h) =>
      h._id === habitId ? { ...h, ...(typeof patch === 'function' ? patch(h) : patch) } : h,
    ),
  )
}

// nodes.setTaskStatus: done stamps completedAt, anything else clears it.
export function optimisticTaskStatus(
  store: OptimisticLocalStore,
  { taskId, status }: { taskId: Id<'nodes'>; status: 'backlog' | 'in-progress' | 'done' },
) {
  const now = Date.now()
  patchNode(store, taskId, (prev) => {
    if (status === 'done')
      return prev.completedAt != null
        ? {}
        : {
            status,
            completedAt: now,
            ...(prev.streakDays != null && { streakDays: prev.streakDays + 1 }),
          }
    if (prev.completedAt == null) return { status }
    return {
      status,
      completedAt: null,
      ...(prev.streakDays != null && { streakDays: Math.max(0, prev.streakDays - 1) }),
    }
  })
  optimisticHabitCompletion(store, taskId as string, status === 'done')
}

// nodes.create under a goal/milestone/target: a placeholder row in the goal trees.
// The temp id is never a real document id; the server row replaces it on sync.
type Milestone = Tree['milestones'][number]
type Target = Milestone['targets'][number]
type TreeTask = Target['tasks'][number]

export function insertTempTreeNode(
  store: OptimisticLocalStore,
  { parentId, type, title }: { parentId: Id<'nodes'>; type: string; title: string },
) {
  const _id = `optimistic-${crypto.randomUUID()}` as Id<'nodes'>
  const task: TreeTask = {
    _id,
    title,
    description: undefined,
    effort: 'medium',
    superUrgent: undefined,
    createdAt: Date.now(),
    completedAt: null,
    status: 'backlog',
    dueDate: undefined,
    recurs: undefined,
    recursPeriod: undefined,
    recursDays: undefined,
    recurrenceDay: undefined,
    recurrenceTime: undefined,
    durationMinutes: undefined,
    allDay: true,
  }
  const target: Target = { _id, title, progress: 0, tasks: [] }
  const milestone: Milestone = {
    _id,
    title,
    progress: 0,
    completedAt: null,
    dueDate: undefined,
    targets: [],
  }
  for (const { args, value } of store.getAllQueries(api.nodes.getGoalTree)) {
    if (!value) continue
    const milestones: Milestone[] =
      type === 'milestone' && value._id === parentId
        ? [...value.milestones, milestone]
        : value.milestones.map((ms) => ({
            ...ms,
            targets:
              type === 'target' && ms._id === parentId
                ? [...ms.targets, target]
                : ms.targets.map((tg) =>
                    type === 'task' && tg._id === parentId
                      ? { ...tg, tasks: [...tg.tasks, task] }
                      : tg,
                  ),
          }))
    store.setQuery(api.nodes.getGoalTree, args, { ...value, milestones })
  }
}
